import type { baloot } from "@app/rules";
import type { ToastData } from "../components/Toast";
import type { T } from "./tableText";
import { type Seats, teamsLine } from "./toastParts";

/** Baloot in words: the call, the seat line with its projects, the hand-end toast. */

/** A Baloot call as players say it: "Sun", "Hokum Hearts Double". */
export function balootCallText(
  call: { mode: "sun" | "hokum"; trump: string | null; multiplier?: number },
  t: T,
): string {
  const times =
    (call.multiplier ?? 1) > 1
      ? ` ${t(`baloot.double.${call.multiplier}` as "baloot.double.2")}`
      : "";
  if (call.mode === "sun") return t("baloot.call.sun");
  return `${t("baloot.call.hokum")} ${t(`suit.${call.trump ?? "spades"}` as "suit.spades")}${times}`;
}

/** A seat's projects as short labels (Sira, 50, 100, 400). */
function projectLabels(view: baloot.BalootView, seat: number, t: T): string[] {
  return view.projects
    .filter((project) => project.seat === seat)
    .map((project) => t(`baloot.project.${project.kind}` as "baloot.project.sira"));
}

function balootCall(view: baloot.BalootView, seat: number) {
  if (view.contract?.seat === seat) return view.contract;
  const standing = view.bidding?.call ?? null;
  return standing?.seat === seat ? standing : null;
}

/** A Baloot seat's call, projects and tricks, joined; empty when there is nothing yet. */
function balootLine(view: baloot.BalootView, seat: number, t: T): string {
  const call = balootCall(view, seat) ?? view.doubling?.pending ?? null;
  const parts = [
    call?.seat === seat ? balootCallText(call, t) : null,
    ...projectLabels(view, seat, t),
  ];
  if (view.contract !== null)
    parts.push(t("table.seat.tricks", { tricks: view.tricksWon[seat] ?? 0 }));
  return parts.filter((p) => p !== null).join(" · ");
}

export function balootSeatSub(
  view: baloot.BalootView,
  seat: number,
  yourSeat: number | null,
  t: T,
): string {
  const deciding = view.turn === seat && (view.phase === "bidding" || view.phase === "doubling");
  if (deciding) return seat === yourSeat ? t("table.bid.you") : t("table.seat.thinking");
  const line = balootLine(view, seat, t);
  if (line !== "") return line;
  return view.bidding?.passed[seat] ? t("table.seat.pass") : t("table.seat.waiting");
}

/** Baloot: the call and whether it held, a clean sweep, the team deltas. */
export function balootHandEnd(view: baloot.BalootView, seats: Seats, t: T): ToastData | null {
  const result = view.lastHandResult;
  if (result === null) return null;
  const { seat } = result.contract;
  const sweep = result.kaboot === null ? [] : [t("baloot.event.kaboot")];
  return {
    title: t(result.made ? "baloot.handEnd.made" : "baloot.handEnd.failed", {
      name: seats[seat]?.name ?? "?",
      call: balootCallText(result.contract, t),
    }),
    sub: [...sweep, teamsLine(result.deltas, view.yourSeat, t)].join(" · "),
    tone: result.made ? "success" : "error",
    member: seats[seat] ?? null,
  };
}
